import { Badge, Box, CircularProgress, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import React from 'react'

export interface ContractActionButtonProps {
  /** The icon to display */
  icon: React.ReactNode
  /** Tooltip text or element */
  tooltip?: React.ReactNode
  /** Tooltip placement */
  tooltipPlacement?: 'left' | 'right' | 'top' | 'bottom'
  /** Click handler */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  /** Whether the action is in progress (shows loading spinner) */
  isLoading?: boolean
  /** Whether the button is disabled */
  disabled?: boolean
  /** Whether the action is completed (applies success styling) */
  isCompleted?: boolean
  /** Badge content to show (e.g., AddIcon for create actions) */
  badgeContent?: React.ReactNode
  /** Custom width */
  width?: number
  /** Custom height */
  height?: number
  /** Custom icon size */
  iconSize?: number
  /** Custom badge size */
  badgeSize?: number
  /** Whether to show error styling */
  showError?: boolean
  /** Custom sx props for IconButton */
  sx?: object
}

const ContractActionButton: React.FC<ContractActionButtonProps> = ({
  icon,
  tooltip,
  tooltipPlacement = 'left',
  onClick,
  isLoading = false,
  disabled = false,
  isCompleted: _isCompleted = false,
  badgeContent,
  width = 48,
  height = 48,
  iconSize: _iconSize = 38,
  badgeSize = 16,
  showError: _showError = false,
  sx = {},
}) => {
  const theme = useTheme()

  // Scale CircularProgress relative to button size
  const spinnerSize = Math.round(width * 0.3)

  const isDisabled = disabled || isLoading

  const buttonContent = (
    <IconButton
      onClick={onClick}
      disabled={isDisabled}
      sx={{
        width,
        height,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        cursor: isDisabled ? 'default' : 'pointer',
        '&:hover': {
          backgroundColor: 'transparent',
          opacity: isDisabled ? 1 : 0.7,
        },
        '&.Mui-disabled': {
          opacity: 1,
        },
        ...sx,
      }}
    >
      {isLoading ? (
        <CircularProgress
          size={spinnerSize}
          sx={{
            color: theme.palette.text.secondary,
          }}
        />
      ) : badgeContent ? (
        <Badge
          badgeContent={badgeContent}
          color="secondary"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: 'transparent',
              minWidth: badgeSize,
              height: badgeSize,
            },
          }}
        >
          {icon}
        </Badge>
      ) : (
        icon
      )}
    </IconButton>
  )

  if (!tooltip) {
    return buttonContent
  }

  return (
    <Tooltip
      title={typeof tooltip === 'string' ? <Typography sx={{ fontSize: 16 }}>{tooltip}</Typography> : tooltip}
      placement={tooltipPlacement}
    >
      <Box
        sx={{
          cursor: isDisabled ? 'default' : 'pointer',
          '&:hover': { opacity: isDisabled ? 1 : 0.6 },
        }}
      >
        {buttonContent}
      </Box>
    </Tooltip>
  )
}

export default ContractActionButton
